import React, { useLayoutEffect, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiAlertTriangle, FiCheckCircle, FiShield, FiAlertCircle, FiExternalLink, FiPhone } from "react-icons/fi";
import slideTo, { scrollToTop } from "./pageTransition";
import useConsultPopup from "./useConsultPopup";
import ConsultPopup from "./ConsultPopup";
import insights from "../data/insights.json";
import "./page-transition.css";
import "./insights.css";

const severityColors = {
  "긴급": "#dc2626",
  "높음": "#ea580c",
  "주의": "#ca8a04",
  "참고": "#2563eb",
};

function formatDate(dateStr) {
  const [year, month, day] = dateStr.split("-");
  return `${year}년 ${parseInt(month)}월 ${parseInt(day)}일`;
}

function generateJsonLd(item) {
  const article = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": item.title,
    "description": item.summary,
    "datePublished": item.date,
    "dateModified": item.date,
    "author": {
      "@type": "Organization",
      "name": "솔컴바인",
      "url": "https://solcombine.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "솔컴바인",
      "url": "https://solcombine.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://solcombine.com/logo512.png"
      }
    },
    "keywords": item.keywords?.join(", ") || "",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://solcombine.com/insights/${item.id}`
    }
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "홈",
        "item": "https://solcombine.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "보안·IT 이슈 브리핑",
        "item": "https://solcombine.com/insights"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": item.title,
        "item": `https://solcombine.com/insights/${item.id}`
      }
    ]
  };

  return [article, breadcrumb];
}

export default function InsightDetail() {
  const navigate = useNavigate();
  const { insightId } = useParams();
  const { consultOpen, consult, isSending, openConsult, closeConsult, updateConsult, submitConsult } = useConsultPopup({ defaultService: "기존 시스템 전담 운영" });

  const item = insights.find((i) => i.id === insightId);
  const currentIndex = insights.findIndex((i) => i.id === insightId);

  useLayoutEffect(() => {
    scrollToTop();
  }, [insightId]);

  useEffect(() => {
    if (!item) return;

    document.title = `${item.title} | 보안 브리핑 | SOLCOMBINE`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", item.summary);
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", `https://solcombine.com/insights/${item.id}`);
    }

    // Add JSON-LD
    const existingScripts = document.querySelectorAll('script[data-insight-jsonld]');
    existingScripts.forEach((s) => s.remove());

    const jsonLdData = generateJsonLd(item);
    jsonLdData.forEach((data) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-insight-jsonld", "true");
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      const scripts = document.querySelectorAll('script[data-insight-jsonld]');
      scripts.forEach((s) => s.remove());
    };
  }, [item]);

  const goBack = () => slideTo(navigate, "/insights", undefined, "back");
  const goToItem = (index) => {
    if (index >= 0 && index < insights.length) {
      const nextItem = insights[index];
      slideTo(navigate, `/insights/${nextItem.id}`, undefined, index > currentIndex ? "forward" : "back");
    }
  };

  if (!item) {
    return (
      <div className="insight-page">
        <header className="insight-topbar">
          <button type="button" className="insight-back" onClick={goBack}>
            <FiArrowLeft /> 목록으로
          </button>
          <span className="insight-topbar-brand">SOL<span>COMBINE</span></span>
        </header>
        <main className="insight-body">
          <div className="insight-not-found">
            <FiAlertCircle />
            <h1>이슈를 찾을 수 없습니다</h1>
            <button type="button" onClick={goBack}>목록으로 돌아가기</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="insight-page insight-detail-page">
      <header className="insight-topbar">
        <button type="button" className="insight-back" onClick={goBack}>
          <FiArrowLeft /> 목록으로
        </button>
        <div className="insight-nav-arrows">
          <button
            type="button"
            onClick={() => goToItem(currentIndex - 1)}
            disabled={currentIndex <= 0}
            aria-label="이전 이슈"
          >
            <FiArrowLeft />
          </button>
          <span>{currentIndex + 1} / {insights.length}</span>
          <button
            type="button"
            onClick={() => goToItem(currentIndex + 1)}
            disabled={currentIndex >= insights.length - 1}
            aria-label="다음 이슈"
          >
            <FiArrowRight />
          </button>
        </div>
        <span className="insight-topbar-brand">SOL<span>COMBINE</span></span>
      </header>

      <main className="insight-body">
        <article className="insight-detail">
          <header className="insight-detail-header">
            <div className="insight-detail-meta">
              <span
                className="insight-severity"
                style={{ "--severity-color": severityColors[item.severity] || "#6b7280" }}
              >
                <FiAlertTriangle /> {item.severity}
              </span>
              <span className="insight-category">{item.category}</span>
              <time className="insight-date">{formatDate(item.date)}</time>
            </div>
            <h1>{item.title}</h1>
            <p className="insight-summary">{item.summary}</p>
          </header>

          {item.what?.length > 0 && (
            <section className="insight-section">
              <h2><FiAlertCircle /> 무슨 일인가요?</h2>
              <ul>
                {item.what.map((text, i) => <li key={i}>{text}</li>)}
              </ul>
            </section>
          )}

          {item.affected?.length > 0 && (
            <section className="insight-section insight-affected">
              <h2><FiAlertTriangle /> 해당되는 곳</h2>
              <ul>
                {item.affected.map((text, i) => <li key={i}>{text}</li>)}
              </ul>
            </section>
          )}

          {item.check?.length > 0 && (
            <section className="insight-section insight-check">
              <h2><FiCheckCircle /> 확인 방법</h2>
              <ol>
                {item.check.map((step, i) => (
                  <li key={i}>
                    <strong>{step.title}</strong>
                    <p>{step.detail}</p>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {item.prevent?.length > 0 && (
            <section className="insight-section insight-prevent">
              <h2><FiShield /> 예방 조치</h2>
              <ol>
                {item.prevent.map((step, i) => (
                  <li key={i}>
                    <strong>{step.title}</strong>
                    <p>{step.detail}</p>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {item.if_compromised?.length > 0 && (
            <section className="insight-section insight-compromised">
              <h2><FiPhone /> 이미 피해를 입었다면</h2>
              <ul>
                {item.if_compromised.map((text, i) => <li key={i}>{text}</li>)}
              </ul>
            </section>
          )}

          {item.solcombine_note && (
            <aside className="insight-note">
              <strong>솔컴바인 안내</strong>
              <p>{item.solcombine_note}</p>
              <button type="button" onClick={openConsult}>
                점검 상담받기 <FiArrowRight />
              </button>
            </aside>
          )}

          {item.sources?.length > 0 && (
            <section className="insight-section insight-sources">
              <h2>출처</h2>
              <ul>
                {item.sources.map((source, i) => (
                  <li key={i}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.title} <small>— {source.publisher}</small> <FiExternalLink />
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {item.keywords?.length > 0 && (
            <div className="insight-keywords">
              {item.keywords.map((kw, i) => <span key={i}>{kw}</span>)}
            </div>
          )}
        </article>

        <nav className="insight-detail-nav">
          <button type="button" onClick={goBack}>
            <FiArrowLeft /> 목록으로 돌아가기
          </button>
        </nav>
      </main>

      <footer className="insight-footer">
        <p>솔컴바인은 직접 개발·운영하는 고객사 시스템의 보안을 함께 관리합니다.</p>
      </footer>

      <ConsultPopup
        consultOpen={consultOpen}
        consult={consult}
        isSending={isSending}
        closeConsult={closeConsult}
        updateConsult={updateConsult}
        submitConsult={submitConsult}
      />
    </div>
  );
}
